import React from 'react';
import { spine } from '../spine-webgl';
import { mapSize, WEBGL_ASSET_PATHS } from '../constants/constants';
import "./WebGLContext.scss";
import cx from 'classnames';
import { delay } from '../services/util';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";

var animationId;
var mat4 = require('gl-matrix-mat4');

let FRAME_RATE = 30; // approximate frame rate. this won't be precise because we're still using requestAnimationFrame

const ENABLE_RENDERING = true;
let isMounted = false;
let processingFinished = false;

// used to track the background location for continuous background scrolling
let lastBackgroundX = 0;
let lastBackgroundY = 0;
let avatarHidden = false;
let processingSkeleton = {
  scale: new spine.Vector2(1, 1),
  pos: new spine.Vector2(mapSize.width / 2, mapSize.height / 2),
  spineContent: null,
  depth: 12,
};

let processingEvent = null;

function MyEventTarget() {
  var target = document.createTextNode(null);
  this.addEventListener = target.addEventListener.bind(target);
  this.removeEventListener = target.removeEventListener.bind(target);
  this.dispatchEvent = target.dispatchEvent.bind(target);
}
MyEventTarget.prototype = EventTarget.prototype;

function isiOS13ORLower(){
  if(!navigator.userAgent) return false
  if(!navigator.maxTouchPoints && navigator.maxTouchPoints <= 1) return false
  const isiPadMobile13OrLower = navigator.userAgent.match(/(iPad).*OS\s(12_?\d*|13_?\d*)/gi)
  const isiPadButDesktopVersionRequested = navigator.userAgent.match(/(macintosh.*applewebKit)/gi)
  return isiPadMobile13OrLower || isiPadButDesktopVersionRequested
}

class WebGLContext extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clickablesToRender: [],
      zVal: '',
      background: null,
      className: null,
    };
    // Spines to render is no longer a state because we do not need to rerender the canvas when this changes
    this.spinesToRender = [];
    this.transform = new spine.webgl.Matrix4();
    this.canvas = null;
    this.canvasRef = React.createRef();
    this.gl = null;
    this.shader = null;
    this.batcher = null;
    this.mvp = new spine.webgl.Matrix4();
    this.assetManager = null;
    this.skeletonRenderer = null;
    this.realPixelToWorldScale = 1;
    this.orthoBounds = {
      x: 0,
      y: 0,
      width: 2048,
      height: 1152,
    };
    this.drawImageShaderProgram = null;
    this.visemeArray = [];
    this.gestureArray = [];
    this.amiraPlayAnimations = false;
    this.hideAmira = false;
    this.resetAmira = false;
    processingEvent = isiOS13ORLower() ? new MyEventTarget() : new EventTarget();

    this.init = this.init.bind(this);
    this.load = this.load.bind(this);

    // Any spine which will be used must be loaded here so that we can access it when we want it. Once we have better memory management, this will likely change
    this.loadSpineSkeleton = this.loadSpineSkeleton.bind(this);
    this.loadSkeletonData = this.loadSkeletonData.bind(this);
    this.createSpineInstance = this.createSpineInstance.bind(this);
    this.renderAnimation = this.renderAnimation.bind(this);
    this.resize = this.resize.bind(this);
    this.sendSpineRenderable = this.sendSpineRenderable.bind(this);
    this.removeSpineRenderable = this.removeSpineRenderable.bind(this);
    this.startLoadingAnimation = this.startLoadingAnimation.bind(this);
    this.removeLoadingAnimation = this.removeLoadingAnimation.bind(this);
    this.enabled = true;
  }

  componentWillUnmount() {
    isMounted = false;
    processingSkeleton.spineContent = {
      scale: new spine.Vector2(1, 1),
      pos: new spine.Vector2(mapSize.width / 2, mapSize.height / 2),
      spineContent: null,
      depth: 12,
    };

    // Dispose of webgl releated data
    if (this.assetManager) {
      this.assetManager.removeAll();
    }
    if (this.shader) {
      this.shader.dispose();
    }
    if (this.batcher) {
      this.batcher.dispose();
    }
    if (this.gl.canvas) {
      this.gl.canvas.width = 1;
      this.gl.canvas.height = 1;
    }
    if (this.gl.getExtension('WEBGL_lose_context') && !this.gl.isContextLost()) {
      // Make sure we haven't lost context already
      this.gl.getExtension('WEBGL_lose_context').loseContext();
    }

    // Clear out webgl objects
    this.spinesToRender = [];
    this.canvas = null;
    this.gl = null;
    this.shader = null;
    this.batcher = null;
    this.assetManager = null;
    this.skeletonRenderer = null;
    this.transform = null;
    this.mvp = null;
  }

  componentDidMount() {
    isMounted = true;
    this.init();
    this.resize();
  }

  stopAnimation(){
    cancelAnimationFrame(animationId)
    this.canvasRef.current.style.display = 'none'
  }

  hideAvatar(){
    avatarHidden = true
    this.canvasRef.current.style.display = 'none'
  }

  showAvatar(){
    avatarHidden = false
    this.canvasRef.current.style.display = 'block'
  }

  init() {
    this.canvas = document.getElementById('canvas');
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    var config = { alpha: true };
    this.gl =
      this.canvas.getContext('webgl', config) ||
      this.canvas.getContext('experimental-webgl', config);
    if (!this.gl) {
      alert('WebGL is unavailable.');
      return;
    }

    // Create a simple shader, mesh, model-view-projection matrix
    this.shader = spine.webgl.Shader.newTwoColoredTextured(this.gl);
    this.batcher = new spine.webgl.PolygonBatcher(this.gl);
    // this.resize(); // Init mvp and orthoBounds values

    this.skeletonRenderer = new spine.webgl.SkeletonRenderer(this.gl);
    this.assetManager = new spine.webgl.AssetManager(this.gl);

    this.loadSpineSkeleton(
      WEBGL_ASSET_PATHS.AMIRA_PATH + '.skel',
      WEBGL_ASSET_PATHS.AMIRA_PATH + '.atlas'
    );

    this.timeSinceStart = 0;
    this.lastFrameTime = 0;

    animationId = requestAnimationFrame(this.load);
  }

  loadSpineSkeleton(skeletonPath, atlasPath) {
    this.assetManager.loadBinary(
      skeletonPath,
      () => {},
      (path, error) => {
        console.error(`Path: ${path}, Error: ${error}`);
      }
    );
    this.assetManager.loadTextureAtlas(
      atlasPath,
      () => {},
      (path, error) => {
        console.error(`Path: ${path}, Error: ${error}`);
      }
    );
  }

  loadSkeletonData(skeletonPath, atlasPath) {
    // Load the texture atlas from the AssetManager.
    var atlas = this.assetManager.get(atlasPath);

    if (!atlas) {
      this.loadSpineSkeleton(skeletonPath, atlasPath);
      atlas = this.assetManager.get(atlasPath);
    }

    // Create a AtlasAttachmentLoader that resolves region, mesh, boundingbox and path attachments
    var atlasLoader = new spine.AtlasAttachmentLoader(atlas);

    // Create a SkeletonBinary instance for parsing the .skel file.
    var skeletonBinary = new spine.SkeletonBinary(atlasLoader);

    // Set the scale to apply during parsing, parse the file, and create a new skeleton.
    skeletonBinary.scale = 1;
    return skeletonBinary.readSkeletonData(this.assetManager.get(skeletonPath));
  }

  //TODO: atlas shouldn't just be storycraft for everything, but it is fine that it is
  createSpineInstance(skeletonData, initialAnimation, name, skinName) {
    var skeleton = new spine.Skeleton(skeletonData);

    if (skinName) {
      skeleton.setSkinByName(skinName);
    }

    // skeleton.setSkinByName("Outline");
    // var bounds = this.calculateSetupPoseBounds(skeleton);
    skeleton.setToSetupPose();
    skeleton.updateWorldTransform();
    var localBounds = this.calculateLocalBounds(skeleton);

    // Create an AnimationState, and set the initial animation in looping mode.
    var animationStateData = new spine.AnimationStateData(skeleton.data);
    if (!animationStateData) {
      throw new Error('animationStateData is false.');
    }
    var animationState = new spine.AnimationState(animationStateData);
    if (!animationState) {
      throw new Error('animationState is false.');
    }

    if (initialAnimation) {
      if (name === 'amira') {
        animationState.setAnimation(0, initialAnimation, true);
        animationState.setAnimation(1, 'gestures/Idle', true);
      } else {
        animationState.setAnimation(0, initialAnimation, true); // Setup default anim
      }
    }

    let premultipliedAlpha = false;
    if (name === 'amira') {
      premultipliedAlpha = true;
    }

    // Pack everything up and return to caller.
    return {
      skeleton: skeleton,
      state: animationState,
      x: 0,
      y: 0,
      premultipliedAlpha: premultipliedAlpha,
      localBounds: localBounds,
    };
  }

  load() {
    // Wait until the AssetManager has loaded all resources, then load the skeletons.
    if (this.assetManager.isLoadingComplete()) {
      console.log('Finished loading assets');
      console.log("calling load()")
      animationId = requestAnimationFrame(this.renderAnimation);
    } else {
      animationId = requestAnimationFrame(this.load);
    }
  }

  resize() {
    this.canvas.width = 2048;
    this.canvas.height = 1152;

    // Normalized background dimensions:
    // 2048x1152 is the resolution used for campaign maps, so it makes sense to use as
    // our world size as well. We could have custom world sizes depending on the screen
    // shown, but most other content will probably be placed by pixel coordinate or screen
    // percent so it seems simpler to keep it constant.
    var backgroundWidth = 2048;
    var backgroundHeight = 1152;
    var backgroundAspect = backgroundWidth / backgroundHeight;

    // Bounded ortho view
    this.orthoBounds.width = backgroundWidth;
    this.orthoBounds.height = backgroundHeight;
    var screenAspect = 2048 / 1152;
    if (backgroundAspect > screenAspect) {
      // Background image will be cropped with gutters on left and right
      this.orthoBounds.width = this.orthoBounds.height * screenAspect;
    } else {
      // Gutters on top and bottom
      this.orthoBounds.height = this.orthoBounds.width / screenAspect;
    }
    this.orthoBounds.x = (backgroundWidth - this.orthoBounds.width) / 2;
    this.orthoBounds.y = (backgroundHeight - this.orthoBounds.height) / 2;
    this.realPixelToWorldScale = this.orthoBounds.width / this.canvas.width;

    this.mvp.ortho2d(
      this.orthoBounds.x,
      this.orthoBounds.y,
      this.orthoBounds.width,
      this.orthoBounds.height
    );
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  // TODO: pure function
  calculateLocalBounds(skeleton) {
    var offset = new spine.Vector2();
    var size = new spine.Vector2();
    skeleton.getBounds(offset, size, []);

    return { offset: offset, size: size };
  }

  drawBackground(secondsElapsed) {
    let { translateXandY, texture } = this.state.background;
    const backgroundGLWidth = 9; //width of the background in webgl units. In the future we should convert everything to either world or pixles

    let numImages = 3; //Math.ceil(this.canvas.width / backgroundImage.width) + 1;
    let { newX, newY } = translateXandY(secondsElapsed);

    // draw 3 images at appropriate offsets, one behind, one infront, and one current.
    //  Start at -1 so that we have 1 behind us in case we move backwards off the edge of the curent image
    for (var i = -1; i < numImages - 1; i++) {
      this.drawImage(texture, i * backgroundGLWidth - newX, newY);
    }
  }

  renderAnimation() {
    if(this.canvasRef.current && this.canvasRef.current.style.display === 'none' && !avatarHidden){
      this.canvasRef.current.style.display = 'block'
    }

    // console.log("in renderAnimation. isMounted: ", isMounted);
    if (isMounted && this.enabled) {
      //console.log("webgl is mounted. this.state.background: ", this.state.background);
      var now = Date.now() / 1000;
      var delta = 0;
      if (!(this.lastFrameTime === 0)) {
        delta = now - this.lastFrameTime;
        // manually enforce a specific frame rate, if it is slower than the device's refresh rate
        if (delta * 1000 < 1000 / FRAME_RATE) {
          // console.log("requestAnimationFrame early")
          animationId = requestAnimationFrame(this.renderAnimation);
          return;
        }
      }
      this.lastFrameTime = now;
      this.timeSinceStart += delta;

      // Update the MVP matrix to adjust for canvas size changes

      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);

      if (this.state.background) {
        this.drawBackground(delta);
      }

      for (let i = 0; i < this.spinesToRender.length; i++) {
        var renderable = this.spinesToRender[i].spineContent;
        let scale = this.spinesToRender[i].scale;
        let pos = this.spinesToRender[i].pos;

        var skeleton = renderable.skeleton;
        var state = renderable.state;
        var premultipliedAlpha = renderable.premultipliedAlpha;

        if (this.spinesToRender[i].name && this.spinesToRender[i].name === 'amira') {
          if (this.resetAmira) {
            this.timeSinceStart = 0;
            this.lastFrameTime = 0;
            state.setAnimation(0, 'visemes/viseme_sil', true);
            state.setAnimation(1, 'gestures/Idle', true);
            this.resetAmira = false;
          }

          // If there are visemes to play, play them at the correct time
          if (this.visemeArray.length !== 0 && this.amiraPlayAnimations) {
            let currViseme = this.visemeArray[0];
            if (currViseme.time <= this.timeSinceStart * 1000) {
              this.visemeArray.shift();
              state.setAnimation(0, 'visemes/viseme_' + currViseme.value, false);
            }
          }

          // If there are gestures to play, play them at the correct time
          if (this.gestureArray.length !== 0 && this.amiraPlayAnimations) {
            let currGesture = this.gestureArray[0];
            if (currGesture.time <= this.timeSinceStart * 1000) {
              this.gestureArray.shift();
              //console.log("Gesture: " + currGesture.value + " - Gesture Time: " + currGesture.time + " - Actual Time: " + this.timeSinceStart * 1000);
              if (currGesture.value !== 'endPhrase') {
                state.setAnimation(1, currGesture.value, false);
              }
            }
          }

          if (
            this.visemeArray.length === 0 &&
            this.gestureArray.length === 0 &&
            this.amiraPlayAnimations
          ) {
            state.setAnimation(0, 'visemes/viseme_sil', true);
            state.setAnimation(1, 'gestures/Idle', true);
            this.amiraPlayAnimations = false;
          }
        }

        // Apply the animation state based on the delta time in seconds.
        state.update(delta);
        state.apply(skeleton);
        skeleton.updateWorldTransform();

        // Bind the shader and set the texture and model-view-projection matrix.
        this.shader.bind();
        this.shader.setUniformi(spine.webgl.Shader.SAMPLER, 0);

        this.transform.identity();
        this.transform.values[spine.webgl.M00] = scale.x;
        this.transform.values[spine.webgl.M11] = scale.y;
        this.transform.translate(pos.x, pos.y, 0);
        this.transform.multiplyLeft(this.mvp);
        this.shader.setUniform4x4f(spine.webgl.Shader.MVP_MATRIX, this.transform.values); //TODO: call this from the draw function

        // Start the batch and tell the SkeletonRenderer to render the active skeleton.
        if (ENABLE_RENDERING) {
          this.batcher.begin(this.shader);
          this.skeletonRenderer.premultipliedAlpha = premultipliedAlpha;
          this.skeletonRenderer.draw(this.batcher, skeleton);
          this.batcher.end();
        }

        this.shader.unbind();
      }
      animationId = requestAnimationFrame(this.renderAnimation);
    }
  }

  sendSpineRenderable(spineRenderable) {
    this.spinesToRender = [...this.spinesToRender, spineRenderable];
    this.spinesToRender.sort(function (a, b) {
      let depthA = a.depth ? a.depth : 0;
      let depthB = b.depth ? b.depth : 0;
      return depthA - depthB;
    });
  }

  removeSpineRenderable(spineRenderable) {
    for (let i = 0; i < this.spinesToRender.length; i++) {
      if (this.spinesToRender[i] === spineRenderable) {
        this.spinesToRender.splice(i, 1);
      }
    }
  }

  startLoadingAnimation() {
    this.sendSpineRenderable(processingSkeleton);
  }

  async removeLoadingAnimation() {
    processingFinished = true;
    await new Promise((resolve, reject) => {
      processingEvent.addEventListener('finished', function resolveProcessingPromise() {
        processingEvent.removeEventListener('finished', resolveProcessingPromise);
        resolve();
      });
    });
  }

  // originally from https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Tutorial/Adding_2D_content_to_a_WebGL_context
  // creates a shader program to use the image texture to fill the image box
  initDrawImageShaderProgram() {
    // Vertex shader program
    const vsSource = `
      attribute vec4 aVertexPosition;
      attribute vec2 aTextureCoord;

      uniform mat4 uModelViewMatrix;
      uniform mat4 uProjectionMatrix;

      varying highp vec2 vTextureCoord;

      void main(void) {
        gl_Position = uProjectionMatrix * uModelViewMatrix * aVertexPosition;
        vTextureCoord = aTextureCoord;
      }
    `;

    const fsSource = `
      varying highp vec2 vTextureCoord;

      uniform sampler2D uSampler;

      void main(void) {
        gl_FragColor = texture2D(uSampler, vTextureCoord);
      }
    `;

    const vertexShader = this.loadShader(this.gl.VERTEX_SHADER, vsSource);
    const fragmentShader = this.loadShader(this.gl.FRAGMENT_SHADER, fsSource);

    // Create the shader program

    const shaderProgram = this.gl.createProgram();
    this.gl.attachShader(shaderProgram, vertexShader);
    this.gl.attachShader(shaderProgram, fragmentShader);
    this.gl.linkProgram(shaderProgram);

    // If creating the shader program failed, alert

    if (!this.gl.getProgramParameter(shaderProgram, this.gl.LINK_STATUS)) {
      alert('Unable to initialize the shader program: ' + this.gl.getProgramInfoLog(shaderProgram));
      return null;
    }

    this.drawImageShaderProgram = shaderProgram;
  }

  // originally from https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Tutorial/Adding_2D_content_to_a_WebGL_context
  // creates a shader of the given type, uploads the source and
  // compiles it.
  //
  loadShader(type, source) {
    const shader = this.gl.createShader(type);

    // Send the source to the shader object

    this.gl.shaderSource(shader, source);

    // Compile the shader program

    this.gl.compileShader(shader);

    // See if it compiled successfully

    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      alert('An error occurred compiling the shaders: ' + this.gl.getShaderInfoLog(shader));
      this.gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  initDrawImageBuffers() {
    // Create a buffer for the square's positions.

    const positionBuffer = this.gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.

    const positions = [-4.5, 3.5, 4.5, 3.5, -4.5, -3.5, 4.5, -3.5];

    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(positions), this.gl.STATIC_DRAW);

    // Create a buffer for texture coords
    var texcoordBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, texcoordBuffer);

    // Put texcoords in the buffer
    // these are the four corners of the rectangular image. They need to be in the right order to produce the rectange we would expect
    var texcoords = [
      //other orders:  1,2,3,4,(mirror image)  1,3,2,4,(sideways)  1,4,2,3,(sideways and warped), 2,1,3,4,(warped)
      -0,
      -0, // 2
      1,
      -0, // 1
      -0,
      1, // 4
      1,
      1, // 3
    ];
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(texcoords), this.gl.STATIC_DRAW);

    return {
      position: positionBuffer,
      //color: colorBuffer, //this would be used to color the rectangle rather than using our image to fill it
      textureCoord: texcoordBuffer,
    };
  }

  drawScene(programInfo, buffers, texture, xtranslation, ytranslation) {
    this.gl.depthFunc(this.gl.LEQUAL); // Near things obscure far things

    // Create a perspective matrix, a special matrix that is
    // used to simulate the distortion of perspective in a camera.
    // Our field of view is 45 degrees, with a width/height
    // ratio that matches the display size of the canvas
    // and we only want to see objects between 0.1 units
    // and 100 units away from the camera.

    const fieldOfView = (45 * Math.PI) / 180; // in radians
    const aspect = this.gl.canvas.clientWidth / this.gl.canvas.clientHeight;
    const zNear = 0.1;
    const zFar = 100.0;
    const projectionMatrix = mat4.create();

    // note: glmatrix.js always has the first argument
    // as the destination to receive the result.
    mat4.perspective(projectionMatrix, fieldOfView, aspect, zNear, zFar);

    // Set the drawing position to the "identity" point, which is
    // the center of the scene.
    const modelViewMatrix = mat4.create();

    // Now move the drawing position a bit to where we want to
    // start drawing the square.

    mat4.translate(
      modelViewMatrix, // destination matrix
      modelViewMatrix, // matrix to translate
      [xtranslation, ytranslation * -1 + 1, -6.0]
    ); // amount to translate

    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    {
      const numComponents = 2; // pull out 2 values per iteration
      const type = this.gl.FLOAT; // the data in the buffer is 32bit floats
      const normalize = false; // don't normalize
      const stride = 0; // how many bytes to get from one set of values to the next
      // 0 = use type and numComponents above
      const offset = 0; // how many bytes inside the buffer to start from
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffers.position);
      this.gl.vertexAttribPointer(
        programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      this.gl.enableVertexAttribArray(programInfo.attribLocations.vertexPosition);
    }

    // Tell WebGL how to pull out the colors from the color buffer
    // into the vertexColor attribute.

    // tell webgl how to pull out the texture coordinates from buffer
    {
      const num = 2; // every coordinate composed of 2 values
      const type = this.gl.FLOAT; // the data in the buffer is 32 bit float
      const normalize = false; // don't normalize
      const stride = 0; // how many bytes to get from one set to the next
      const offset = 0; // how many bytes inside the buffer to start from
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffers.textureCoord);
      this.gl.vertexAttribPointer(
        programInfo.attribLocations.textureCoord,
        num,
        type,
        normalize,
        stride,
        offset
      );
      this.gl.enableVertexAttribArray(programInfo.attribLocations.textureCoord);
    }

    // Tell WebGL to use our program when drawing

    this.gl.useProgram(programInfo.program);

    // Set the shader uniforms

    this.gl.uniformMatrix4fv(
      programInfo.uniformLocations.projectionMatrix,
      false,
      projectionMatrix
    );
    this.gl.uniformMatrix4fv(programInfo.uniformLocations.modelViewMatrix, false, modelViewMatrix);

    // Tell WebGL we want to affect texture unit 0
    this.gl.activeTexture(this.gl.TEXTURE0);

    // Bind the texture to texture unit 0
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);

    // Tell the shader we bound the texture to texture unit 0
    this.gl.uniform1i(programInfo.uniformLocations.uSampler, 0);

    {
      const offset = 0;
      const vertexCount = 4;
      this.gl.drawArrays(this.gl.TRIANGLE_STRIP, offset, vertexCount);
    }
  }

  // adapted in part from https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/Tutorial/Using_textures_in_WebGL
  drawImage(texture, x, y) {
    //console.log("in drawImage. texture: ", texture, ", x: ", x, ", y: ", y);
    //let texture = this.loadImageAndCreateTextureInfo(url);
    //console.log("texture: ", texture);

    if (!this.drawImageShaderProgram) {
      this.initDrawImageShaderProgram();
    }
    const shaderProgram = this.drawImageShaderProgram;
    const buffers = this.initDrawImageBuffers();

    const programInfo = {
      program: shaderProgram,
      attribLocations: {
        vertexPosition: this.gl.getAttribLocation(shaderProgram, 'aVertexPosition'),
        textureCoord: this.gl.getAttribLocation(shaderProgram, 'aTextureCoord'),
      },
      uniformLocations: {
        projectionMatrix: this.gl.getUniformLocation(shaderProgram, 'uProjectionMatrix'),
        modelViewMatrix: this.gl.getUniformLocation(shaderProgram, 'uModelViewMatrix'),
        uSampler: this.gl.getUniformLocation(shaderProgram, 'uSampler'),
      },
    };

    this.drawScene(programInfo, buffers, texture, x, y);

    //vertices array. Format per line: x,y,r,g,b,a,u,v,r2,g2,b2,a2
  }

  // creates a texture info { width: w, height: h, texture: tex }

  render() {
    // This is the object that is passed down to all children (every component except speech) of WebGLContext
    const propsToPassDown = {
      stopAnimation: this.stopAnimation,
      loadSkeletonData: (skeletonPath, atlasPath) => this.loadSkeletonData(skeletonPath, atlasPath),
      createSpineInstance: (skeletonData, initialAnimation, name, skinName) =>
        this.createSpineInstance(skeletonData, initialAnimation, name, skinName),
      sendSpineRenderable: (spineRenderable) => this.sendSpineRenderable(spineRenderable),
      removeSpineRenderable: (spineRenderable) => this.removeSpineRenderable(spineRenderable),
      moveWebglLayer: (val) => this.setState({ zVal: val }),
      startLoadingAnimation: () => this.startLoadingAnimation(),
      removeLoadingAnimation: () => this.removeLoadingAnimation(),
      sendAmiraVisemeArray: (newVisemeArray) => {
        this.visemeArray = newVisemeArray;
      },
      sendAmiraGestureArray: (newGestureArray) => {
        this.gestureArray = newGestureArray;
      },
      setAmiraPlayAnimations: (playAnimations) => {
        this.amiraPlayAnimations = playAnimations;
      },
      resetAmiraAnimations: () => {
        this.resetAmira = true;
      },
      setClassName: (className) => this.setState({ className }),
      isEnabled: this.getIsEnabled,
      context: this.gl,
      canvasRef: this.canvasRef,
      drawImage: this.drawImage.bind(this),
      resize: this.resize,
      hideAvatar: this.hideAvatar,
      showAvatar: this.showAvatar,
    };

    let canvasDivClasses = 'webgl-div';
    if (this.state.className) {
      canvasDivClasses += ' ' + this.state.className;
    }

    const canvasClass = cx({
      webGLCanvas: true,
      [this.state.zVal]: !!this.state.zVal,
      hidden: this.hideAmira
    })

    // zIndex was originally 1 - I set it to 12 to show it during the grad experience
    return (
      <div className={canvasDivClasses}
           {...doNotUseAccessibleStaticElementInteractions({onDragStart: e => e.preventDefault()})}>
        <canvas id="canvas" ref={this.canvasRef} className={canvasClass} />
        {this.props.children(propsToPassDown)}

        {this.state.clickablesToRender &&
          this.state.clickablesToRender.map((div, index) => {
            return div.renderable;
          })}
      </div>
    );
  }
}

export default WebGLContext;
